import React, { createContext, useState, useEffect, useContext, useMemo } from 'react';
import UrlConfig from '../data/UrlConfig';

const GUEST_PROGRESS_KEY = 'manifiesta_guest_progress';
const PENDING_REVELATION_KEY = 'manifiesta_pending_revelation'; // --- NUEVA CLAVE ---

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const urlConfig = UrlConfig;

  const [guestProgress, setGuestProgress] = useState({ completedSlugs: new Set(), activityData: {} });

  useEffect(() => {
    const savedGuestProgress = localStorage.getItem(GUEST_PROGRESS_KEY);
    if (savedGuestProgress) {
      const parsed = JSON.parse(savedGuestProgress);
      setGuestProgress({ ...parsed, activityData: parsed.activityData || {}, completedSlugs: new Set(parsed.completedSlugs || []) });
    }
  }, []);

  const saveGuestActivityProgress = (activitySlug, data) => {
    setGuestProgress(prevProgress => {
      const newProgress = {
        completedSlugs: new Set(prevProgress.completedSlugs).add(activitySlug),
        activityData: {
          ...prevProgress.activityData,
          [activitySlug]: data,
        }
      };
      localStorage.setItem(GUEST_PROGRESS_KEY, JSON.stringify({
        ...newProgress,
        completedSlugs: Array.from(newProgress.completedSlugs)
      }));
      return newProgress;
    });
  };

  const getGuestActivityData = (activitySlug) => {
    return guestProgress.activityData[activitySlug] || null;
  };
  
  // --- NUEVA FUNCIÓN PARA GUARDAR REVELACIÓN PENDIENTE ---
  const savePendingRevelation = async () => {
    const pendingRaw = sessionStorage.getItem(PENDING_REVELATION_KEY);
    if (!pendingRaw) return;

    try {
        const { revelation, totalTimeMeditated } = JSON.parse(pendingRaw);

        const response = await fetch(urlConfig.meditation_saveRevelation, {
            method: 'POST',
            credentials: 'include', // Esencial, ya que el usuario acaba de iniciar sesión
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ revelation, totalTimeMeditated }),
        });

        if (response.ok) {
            sessionStorage.removeItem(PENDING_REVELATION_KEY);
        } else {
            const errorResult = await response.json();
            console.error("Fallo al guardar la revelación pendiente en el backend:", errorResult.message);
        }
    } catch (error) {
        console.error("Error de red al procesar la revelación pendiente:", error);
    }
  };
  
  const syncGuestProgressWithBackend = async () => {
    const guestDataRaw = localStorage.getItem(GUEST_PROGRESS_KEY);
    if (!guestDataRaw) return;


    try {
      const response = await fetch(urlConfig.sync_guest_progress, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: guestDataRaw,
      });

      if (response.ok) {
        localStorage.removeItem(GUEST_PROGRESS_KEY);
        setGuestProgress({ completedSlugs: new Set(), activityData: {} });
      } else {
        const result = await response.json();
        console.error("Fallo al sincronizar el progreso del invitado:", result.message);
      }
    } catch (error) {
      console.error("Error de red al sincronizar el progreso de invitado:", error);
    }
  };

  useEffect(() => {
    const checkLoggedInUser = async () => {
      setIsLoading(true);
      try {
        const res = await fetch(urlConfig.verify_token, { credentials: 'include' });
        
        if (res.ok) {
          const data = await res.json();
          if (data.success) {
            setUser(data.data);
          } else {
            setUser(null);
          }
        } else {
          setUser(null);
        }
      } catch (error) {
        console.error("Error al verificar la sesión:", error);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };
    checkLoggedInUser();
  }, [urlConfig.auth_constant]);

  // --- NUEVO USEEFFECT PARA TAREAS POST-LOGIN ---
  useEffect(() => {
    const runPostLoginTasks = async () => {
      if (user && !isLoading) {
        await syncGuestProgressWithBackend();
        await savePendingRevelation();
      }
    };
    runPostLoginTasks();
  }, [user, isLoading]);

  const login = async (loginIdentifier, password, lang) => {
    const res = await fetch(urlConfig.auth_login, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ loginIdentifier, password, lang }),
    });

    const data = await res.json();

    if (!res.ok || !data.success) {
      throw new Error(data.message || 'Error al iniciar sesión.');
    }
    
    // Solo actualizamos el estado. El useEffect se encargará del resto.
    setUser(data.data);
    return data.data;
  };  
  
  const refreshUser = (newUserData) => {
    setUser(newUserData);
  };

  const logout = async () => {
    try {
      await fetch(urlConfig.auth_logout, { method: 'POST', credentials: 'include' });
    } catch (error) {
      console.error("Error al cerrar sesión en el backend:", error);
    } finally {
      setUser(null);
    }
  };

  const signup = async (username, nick, name, email, password, lang) => {
    const res = await fetch(urlConfig.auth_signup, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, nick, name, email, password, lang }),
    });
    const data = await res.json();
    if (!res.ok || !data.success) {
      throw new Error(data.message || 'Ocurrió un error durante el registro.');
    }
    return data;
  };

  const memoedValue = useMemo(() => ({
    user,
    loggedIn: !!user,
    isLoading,
    login,
    logout,
    signup,
    refreshUser,
    setUser,
    guestProgress,
    saveGuestActivityProgress,
    getGuestActivityData,
  }), [user, isLoading, guestProgress]);

  return (
    <AuthContext.Provider value={memoedValue}>
      {!isLoading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};